import React, { useRef, useEffect, useState } from "react";
import useKeyPress from "./useKeyPress";
import AriaLive from "./AriaLive";
import CustomSearchBox from "./CustomSearchBox.jsx";
import EmptyQueryBoundary from "./EmptyQueryBoundary";
import Results from "./Results.jsx";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import { liteClient as algoliasearch } from "algoliasearch/lite";
import { InstantSearch } from "react-instantsearch";

// Translation object
const translations = {
  en: {
    search: "Search",
    close: "Close",
    tip: "Tip: Open search with the keyboard key \"/\"",
    resultsUpdate: "Results will update as you type",
  },
  fr: {
    search: "Recherche",
    close: "Fermer",
    tip: "Astuce : Ouvrez la recherche avec la touche du clavier « / »",
    resultsUpdate: "Les résultats seront mis à jour au fur et à mesure de votre saisie",
  },
};

const lang = document.documentElement.lang || "en";
const t = translations[lang] || translations.en;

const algoliaClient = algoliasearch("IK7IHXCC2V", "dabbf277965b755d7dfd952befed7a5d");
const langFilter = lang === "en" ? "metadata.lang:-fr" : "metadata.lang:-en";

const searchClient = {
  ...algoliaClient,
  search(requests) {
    if (requests.every(({ params }) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: "",
          params: "",
        })),
      });
    }
    // Add langFilter to each request's facetFilters
    const filteredRequests = requests.map((req) => {
      let facetFilters = req.params.facetFilters || [];
      if (typeof facetFilters === "string") {
        facetFilters = [facetFilters];
      }
      facetFilters = [...facetFilters, langFilter];
      return {
        ...req,
        params: {
          ...req.params,
          facetFilters,
        },
      };
    });
    return algoliaClient.search(filteredRequests);
  },
};

export default function Modal({ openModal, setOpenModal }) {
  const modalRef = useRef(null);
  const searchRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [numHits, setNumHits] = useState(null);

  // keyboard navigation for modal
  useKeyPress(
    ["Tab"],
    (pressed, event) => {
      event.preventDefault();
      if (pressed) {
        handleTabNavigation("forward");
      }
    },
    openModal,
    modalRef
  );

  useKeyPress(
    ["Shift", "Tab"],
    (pressed, event) => {
      event.preventDefault();
      if (pressed) {
        handleTabNavigation("backward");
      }
    },
    openModal,
    modalRef
  );

  useKeyPress(["/"], (pressed, event) => {
    if (pressed) {
      event.preventDefault();
      setOpenModal(true);
    }
  });

  // Escape to close modal (only when modal is open)
  useKeyPress(
    ["Escape"],
    (pressed, event) => {
      if (pressed) {
        event.preventDefault();
        event.stopPropagation();
        setOpenModal(false);
      }
    },
    openModal,
    modalRef
  );

  const handleTabNavigation = (direction) => {
    const allFocusableElements = modalRef.current?.querySelectorAll(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );

    const focusableElements = Array.from(allFocusableElements || []).filter((el) => {
      let node = el;
      while (node && node !== modalRef.current) {
        const style = window.getComputedStyle(node);
        if (
          style.display === "none" ||
          style.visibility === "hidden" ||
          node.hasAttribute("hidden") ||
          node.classList.contains("hidden") ||
          node.getAttribute("aria-selected") === "false"
        ) {
          return false;
        }
        node = node.parentElement;
      }
      return true;
    });

    if (focusableElements.length === 0) return;

    const focusedElement = document.activeElement;
    const currentIndex = focusableElements.indexOf(focusedElement);

    if (direction === "forward") {
      const nextIndex = (currentIndex + 1) % focusableElements.length;
      focusableElements[nextIndex].focus();
    } else if (direction === "backward") {
      const prevIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
      focusableElements[prevIndex].focus();
    }
  };

  useGSAP(
    () => {
      document.body.classList.toggle("is-locked", openModal);

      if (openModal) {
        modalRef.current?.showModal();
        searchRef.current?.focus();
      } else {
        modalRef.current?.classList.add("closing");
        gsap.to(modalRef.current, {
          opacity: 0,
          duration: 0.2,
          ease: "power3.out",
          onComplete: () => {
            modalRef.current?.close();
            modalRef.current?.classList.remove("closing");
            gsap.set(modalRef.current, {
              clearProps: true,
            });
          },
        });
      }
    },
    { dependencies: [openModal], scope: modalRef }
  );

  return (
    <dialog className="search-modal" ref={modalRef} onCancel={() => setOpenModal(false)}>
      <div className="content">
        <div className="search-header">
          <h1 className="h2">{t.search}</h1>
          <button
            className="modal-close text-medium"
            ref={closeButtonRef}
            onClick={() => setOpenModal(false)}
          >
            {t.close} <span className="visually-hidden">{t.search}</span>
          </button>
        </div>
        <div className="search-content">
          <InstantSearch searchClient={searchClient} indexName="wp_searchable_posts" insights>
            <div className="search-box">
              <CustomSearchBox inputRef={searchRef} />
              <p className="search-tip text-small">{t.tip}</p>
              <p className="search-tip visually-hidden" id="search-info">
                {t.resultsUpdate}
              </p>
              <AriaLive numResults={numHits} />
              <EmptyQueryBoundary fallback={null}></EmptyQueryBoundary>
            </div>
            <Results setNumHits={setNumHits} />
          </InstantSearch>
        </div>
      </div>
    </dialog>
  );
}
